/**
 * 報酬表 表示用 共通コンポーネント (v3.2.29+ / v3.3.0 で加算方式に反転)。
 *
 * SA 報酬計算機ページ (SaCommissionCalcPage) と、
 * 報酬設定画面のモーダル (CommissionTableViewerDialog / CommissionOverrideTableViewerDialog)
 * の両方から利用する。
 *
 * v3.3.0: 「弊社紹介減額」→「開拓顧客加算」方式に反転。
 *  - ベース報酬率 = 弊社紹介 (Deal.isCompanyReferral=true) の Deal に適用される率
 *  - 開拓顧客 (フラグなし) の Deal はベース + カテゴリー別加算率で計算
 *
 * - `CommissionTableBody`: 行のみのレンダリング (mode: normal / selfDeveloped / parallel に応じて列構成切替)
 * - `buildPrintableHtml`: PDF 出力用 HTML 生成 (window.print() で利用)。columns で出力カラム選択可
 * - `PdfColumnPicker`: PDF 出力カラム選択チップ (v3.3.0+)
 * - `escapeHtml`: HTML エスケープ
 * - `computeRowsCalc`: items[] + allItems + 加算率 lookup → 行ごとの計算結果
 */
import type { CommissionItem, CommissionType } from '@goenhub/shared';
import { calculateCommission } from '@goenhub/shared';

import { Badge } from '@/components/ui/badge';

/**
 * 報酬表の表示モード。
 * - normal: ベース報酬のみ (弊社紹介の Deal に適用される率)
 * - selfDeveloped: 開拓顧客 (ベース + 加算率) のみ
 * - parallel: 両方を並列表示
 */
export type ReferralViewMode = 'normal' | 'selfDeveloped' | 'parallel';

export interface CatalogItemRow {
  id: string;
  priceExcludingTax: number;
}

export interface CommissionTableRowCalc {
  item: CommissionItem;
  product: CatalogItemRow | undefined;
  price: number;
  type: CommissionType;
  /** 開拓顧客加算率 (%)。0 = 加算なし */
  additionRate: number;
  /** ベース報酬額 (弊社紹介時に適用) */
  baseFinal: number;
  /** 開拓顧客報酬額 (ベース + 加算率) */
  selfDevelopedFinal: number;
}

export function computeRowsCalc(args: {
  items: CommissionItem[];
  allItems: CatalogItemRow[];
  defaultType: CommissionType;
  isSaRole: boolean;
  getAdditionRateForProduct: (productId: string) => number;
}): CommissionTableRowCalc[] {
  const { items, allItems, defaultType, isSaRole, getAdditionRateForProduct } = args;
  return items.map((it) => {
    const product = allItems.find((p) => p.id === it.productId);
    const price = product?.priceExcludingTax ?? 0;
    const baseCalc = calculateCommission({
      basePriceExcludingTax: price,
      item: it,
      defaultType,
    });
    // 固定額 (円) の商品は加算の対象外なので、加算率も「—」で出す
    const type = (it.type ?? defaultType) as CommissionType;
    const additionRate = isSaRole && type === 'rate' ? getAdditionRateForProduct(it.productId) : 0;
    const selfDevelopedCalc =
      additionRate > 0
        ? calculateCommission({
            basePriceExcludingTax: price,
            item: it,
            defaultType,
            selfDevelopedAdditionRate: additionRate,
          })
        : baseCalc;
    return {
      item: it,
      product,
      price,
      type,
      additionRate,
      baseFinal: baseCalc.finalAmount,
      selfDevelopedFinal: selfDevelopedCalc.finalAmount,
    };
  });
}

export function CommissionTableBody({
  rows,
  mode,
}: {
  rows: CommissionTableRowCalc[];
  mode: ReferralViewMode;
}) {
  // 商品列は最低 160px を確保し、足りない幅では表ごと横スクロールさせる
  // (ダイアログ幅が狭いと固定幅の列に押されて商品名が「Ti…」まで潰れていたため)
  const baseGrid = 'minmax(160px, 2fr) 110px 60px 80px 80px 80px';
  const gridTemplate =
    mode === 'normal'
      ? `${baseGrid} 110px`
      : mode === 'selfDeveloped'
        ? `${baseGrid} 90px 130px`
        : `${baseGrid} 110px 90px 130px`;
  // 列幅の合計 + 列間 (8px) + 左右余白 (24px)
  const minTableWidth = mode === 'normal' ? 752 : mode === 'selfDeveloped' ? 870 : 988;

  const noPrice = (
    <span className="text-[10px] italic text-[var(--text-muted)] font-normal">価格なし</span>
  );

  return (
    <div className="rounded-md border border-[var(--border)] overflow-x-auto">
      <div style={{ minWidth: minTableWidth }}>
        <div
          className="grid gap-2 px-3 py-2 bg-[var(--bg-elevated)] text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)]"
          style={{ gridTemplateColumns: gridTemplate }}
        >
          <div>商品</div>
          <div className="text-right">税抜価格</div>
          <div>タイプ</div>
          <div className="text-right">値</div>
          <div className="text-right">追加</div>
          <div className="text-right">割引</div>
          {mode === 'normal' && <div className="text-right text-[var(--accent)]">ベース報酬額</div>}
          {mode === 'selfDeveloped' && (
            <>
              <div className="text-right text-emerald-600">加算率</div>
              <div className="text-right text-emerald-700">開拓顧客報酬額</div>
            </>
          )}
          {mode === 'parallel' && (
            <>
              <div className="text-right text-[var(--accent)]">ベース報酬額</div>
              <div className="text-right text-emerald-600">加算率</div>
              <div className="text-right text-emerald-700">開拓顧客報酬額</div>
            </>
          )}
        </div>
        {rows.map((r) => (
          <div
            key={r.item.productId}
            className="grid gap-2 px-3 py-1.5 text-xs border-t border-[var(--border)] items-center"
            style={{ gridTemplateColumns: gridTemplate }}
          >
            <div className="truncate">{r.item.productName}</div>
            <div className="text-right tabular-nums text-[var(--text-secondary)]">
              {r.product ? (
                `¥${r.price.toLocaleString()}`
              ) : (
                <span className="text-[10px] italic text-[var(--text-muted)]">—</span>
              )}
            </div>
            <div>
              <Badge variant="default">{r.type === 'rate' ? '%' : '円'}</Badge>
            </div>
            <div className="text-right tabular-nums">
              {r.type === 'rate' ? `${r.item.value}%` : `¥${r.item.value.toLocaleString()}`}
            </div>
            <div className="text-right tabular-nums text-[var(--text-muted)]">
              +¥{r.item.additionalAmount.toLocaleString()}
            </div>
            <div className="text-right tabular-nums text-[var(--text-muted)]">
              −¥{r.item.discountAmount.toLocaleString()}
            </div>
            {mode === 'normal' && (
              <div className="text-right tabular-nums font-semibold text-[var(--accent)]">
                {r.product ? `¥${r.baseFinal.toLocaleString()}` : noPrice}
              </div>
            )}
            {mode === 'selfDeveloped' && (
              <>
                <div className="text-right tabular-nums text-emerald-600">
                  {r.additionRate > 0 ? `+${r.additionRate}%` : '—'}
                </div>
                <div className="text-right tabular-nums font-semibold text-emerald-700">
                  {r.product ? `¥${r.selfDevelopedFinal.toLocaleString()}` : noPrice}
                </div>
              </>
            )}
            {mode === 'parallel' && (
              <>
                <div className="text-right tabular-nums font-semibold text-[var(--accent)]">
                  {r.product ? `¥${r.baseFinal.toLocaleString()}` : noPrice}
                </div>
                <div className="text-right tabular-nums text-emerald-600">
                  {r.additionRate > 0 ? `+${r.additionRate}%` : '—'}
                </div>
                <div className="text-right tabular-nums font-semibold text-emerald-700">
                  {r.product ? `¥${r.selfDevelopedFinal.toLocaleString()}` : noPrice}
                </div>
              </>
            )}
          </div>
        ))}
        <div className="px-3 py-1.5 text-[10px] text-[var(--text-muted)] bg-[var(--bg-elevated)] border-t border-[var(--border)]">
          ※ 報酬額は税抜価格 × 1 個分の試算。複数台契約や追加割引は計算機タブで確認してください。
          「開拓顧客」= 弊社紹介フラグのない商談 (ベース報酬率 +
          加算率で計算)。弊社紹介の商談はベース報酬額が適用されます。
        </div>
      </div>
    </div>
  );
}

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// ─────────────────────────────────────────────
// v3.3.0+: PDF 出力カラム選択
// ─────────────────────────────────────────────

/** PDF 出力で選択可能なカラムのキー (商品名は常に出力)。 */
export type PdfColumnKey =
  | 'price'
  | 'type'
  | 'value'
  | 'additional'
  | 'discount'
  | 'baseAmount'
  | 'additionRate'
  | 'selfDevelopedAmount';

export const PDF_COLUMN_LABELS: Record<PdfColumnKey, string> = {
  price: '税抜価格',
  type: 'タイプ',
  value: '値',
  additional: '追加',
  discount: '割引',
  baseAmount: 'ベース報酬額',
  additionRate: '開拓顧客加算率',
  selfDevelopedAmount: '開拓顧客報酬額',
};

/** 表示モードごとに PDF 出力対象となり得るカラム一覧。 */
export function pdfColumnsForMode(mode: ReferralViewMode): PdfColumnKey[] {
  const base: PdfColumnKey[] = ['price', 'type', 'value', 'additional', 'discount'];
  if (mode === 'normal') return [...base, 'baseAmount'];
  if (mode === 'selfDeveloped') return [...base, 'additionRate', 'selfDevelopedAmount'];
  return [...base, 'baseAmount', 'additionRate', 'selfDevelopedAmount'];
}

export type PdfColumnSelection = Record<PdfColumnKey, boolean>;

export function defaultPdfColumnSelection(): PdfColumnSelection {
  return {
    price: true,
    type: true,
    value: true,
    additional: true,
    discount: true,
    baseAmount: true,
    additionRate: true,
    selfDevelopedAmount: true,
  };
}

/**
 * PDF 出力カラム選択チップ (v3.3.0+)。
 * 現在の表示モードで出力対象となるカラムだけをチップ表示し、ON/OFF を切替える。
 * 商品名カラムは常に出力されるため選択肢に含めない。
 */
export function PdfColumnPicker({
  mode,
  selection,
  onChange,
}: {
  mode: ReferralViewMode;
  selection: PdfColumnSelection;
  onChange: (next: PdfColumnSelection) => void;
}) {
  const keys = pdfColumnsForMode(mode);
  return (
    <div className="flex items-center gap-1.5 flex-wrap rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] px-2 py-1.5">
      <span className="text-[10px] font-medium text-[var(--text-muted)] mr-0.5">
        PDF 出力カラム:
      </span>
      <span className="px-2 py-0.5 text-[10px] rounded-full border border-[var(--border)] bg-[var(--bg-input)] text-[var(--text-muted)]">
        商品 (固定)
      </span>
      {keys.map((k) => {
        const on = selection[k];
        return (
          <button
            key={k}
            type="button"
            onClick={() => onChange({ ...selection, [k]: !on })}
            className={`px-2 py-0.5 text-[10px] rounded-full border transition-colors ${
              on
                ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)] font-medium'
                : 'border-[var(--border)] bg-[var(--bg-input)] text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
            }`}
            title={on ? 'クリックで PDF から除外' : 'クリックで PDF に含める'}
          >
            {on ? '✓ ' : ''}
            {PDF_COLUMN_LABELS[k]}
          </button>
        );
      })}
    </div>
  );
}

/**
 * 報酬表 PDF 出力用の HTML を構築する。
 * window.open + write でブラウザ標準の印刷ダイアログから PDF 保存を促す。
 */
export function buildPrintableHtml(args: {
  /** ヘッダー部のサブタイトル群 (例: ['セット: 2026年Q1', 'カテゴリー: TimeWaver', '対象: SA']) */
  metaLines: string[];
  /** 表示モード */
  mode: ReferralViewMode;
  /** 行データ */
  rows: CommissionTableRowCalc[];
  /** タイトル (ブラウザタブ表示用) */
  title: string;
  /** 追加情報 (個別設定適用中など) */
  extraNotes?: string[];
  /**
   * 出力カラム (v3.3.0+)。省略時はモードの全カラム。
   * 商品名カラムは常に出力される。
   */
  columns?: PdfColumnKey[];
}): string {
  const { metaLines, mode, rows, title, extraNotes = [] } = args;
  const fmtYen = (n: number) => `¥${Math.round(n).toLocaleString()}`;
  const activeColumns = (args.columns ?? pdfColumnsForMode(mode)).filter((k) =>
    pdfColumnsForMode(mode).includes(k),
  );

  const cols: string[] = ['商品', ...activeColumns.map((k) => PDF_COLUMN_LABELS[k])];

  const cellFor = (r: CommissionTableRowCalc, k: PdfColumnKey): string => {
    switch (k) {
      case 'price':
        return r.product ? fmtYen(r.price) : '—';
      case 'type':
        return r.type === 'rate' ? '%' : '円';
      case 'value':
        return r.type === 'rate' ? `${r.item.value}%` : fmtYen(r.item.value);
      case 'additional':
        return `+${fmtYen(r.item.additionalAmount)}`;
      case 'discount':
        return `−${fmtYen(r.item.discountAmount)}`;
      case 'baseAmount':
        return r.product ? fmtYen(r.baseFinal) : '価格なし';
      case 'additionRate':
        return r.additionRate > 0 ? `+${r.additionRate}%` : '—';
      case 'selfDevelopedAmount':
        return r.product ? fmtYen(r.selfDevelopedFinal) : '価格なし';
    }
  };

  const rowHtml = rows
    .map((r) => {
      const cells = [escapeHtml(r.item.productName), ...activeColumns.map((k) => cellFor(r, k))];
      return `<tr>${cells.map((c) => `<td>${c}</td>`).join('')}</tr>`;
    })
    .join('');

  const today = new Date().toLocaleDateString('ja-JP');
  const metaHtml = [...metaLines, `出力日: ${today}`, ...extraNotes]
    .map((m) => `<div class="meta">${escapeHtml(m)}</div>`)
    .join('');

  return `<!doctype html>
<html lang="ja"><head><meta charset="utf-8"/>
<title>${escapeHtml(title)}</title>
<style>
  * { box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Hiragino Sans', 'Yu Gothic', 'Meiryo', 'Noto Sans CJK JP', sans-serif;
    color: #111;
    margin: 24px;
    font-size: 11px;
  }
  h1 { font-size: 16px; margin: 0 0 4px; }
  .meta { color: #555; font-size: 10px; margin: 2px 0; }
  table {
    width: 100%; border-collapse: collapse; margin-top: 12px;
  }
  th, td {
    border: 1px solid #ccc; padding: 5px 7px; text-align: left;
    font-size: 10.5px;
  }
  th { background: #f3f4f6; font-weight: 600; }
  td:nth-child(n+2) { text-align: right; font-variant-numeric: tabular-nums; }
  td:nth-child(1) { text-align: left; }
  tr:nth-child(even) td { background: #fafafa; }
  .footer { color: #666; font-size: 9.5px; margin-top: 10px; }
  @media print { body { margin: 12mm; } }
</style></head><body>
  <h1>報酬表</h1>
  ${metaHtml}
  <table>
    <thead><tr>${cols.map((c) => `<th>${escapeHtml(c)}</th>`).join('')}</tr></thead>
    <tbody>${rowHtml}</tbody>
  </table>
  <div class="footer">
    ※ 報酬額は税抜価格 × 1 個分の試算。「開拓顧客」= 弊社紹介フラグのない商談 (ベース報酬率 + 加算率で計算)。弊社紹介の商談はベース報酬額が適用されます。
  </div>
  <script>setTimeout(function(){ window.focus(); window.print(); }, 200);</script>
</body></html>`;
}

/**
 * 新規ウィンドウを開いて HTML を書き込み、印刷ダイアログを発火する。
 * ポップアップブロック時は alert で促す。
 */
export function openPrintWindow(html: string): boolean {
  const win = window.open('', '_blank', 'width=1024,height=720');
  if (!win) {
    // eslint-disable-next-line no-alert
    alert('ブラウザがポップアップをブロックしました。許可してから再度お試しください。');
    return false;
  }
  win.document.write(html);
  win.document.close();
  return true;
}

export const REFERRAL_MODE_LABEL: Record<ReferralViewMode, string> = {
  normal: 'ベース報酬',
  selfDeveloped: '開拓顧客 (加算後)',
  parallel: 'ベース / 開拓顧客 並列',
};
